import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const CELL = 15, COLS = 20, ROWS = 20, W = COLS * CELL, H = ROWS * CELL;

export default function Snake() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const dir = useRef({ x: 1, y: 0 });
  const nextDir = useRef({ x: 1, y: 0 });
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");
  const [best, setBest] = useState(() => Number(localStorage.getItem("snake_best") || 0));

  const setD = (x, y) => { if (dir.current.x === -x && dir.current.y === -y) return; nextDir.current = { x, y }; };
  const place = (snake) => {
    let f;
    do { f = { x: (Math.random() * COLS) | 0, y: (Math.random() * ROWS) | 0 }; }
    while (snake.some((p) => p.x === f.x && p.y === f.y));
    return f;
  };
  const start = () => {
    g.current = { snake: [{ x: 8, y: 10 }, { x: 7, y: 10 }, { x: 6, y: 10 }], food: { x: 14, y: 10 }, speed: 7 };
    dir.current = { x: 1, y: 0 }; nextDir.current = { x: 1, y: 0 };
    scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep();
  };

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (["ArrowUp", "w", "W"].includes(k)) setD(0, -1);
      else if (["ArrowDown", "s", "S"].includes(k)) setD(0, 1);
      else if (["ArrowLeft", "a", "A"].includes(k)) setD(-1, 0);
      else if (["ArrowRight", "d", "D"].includes(k)) setD(1, 0);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#0b2b1a"; ctx.fillRect(0, 0, W, H);
      const s = g.current;
      if (s.food) { ctx.fillStyle = "#e8b04b"; ctx.fillRect(s.food.x * CELL + 2, s.food.y * CELL + 2, CELL - 4, CELL - 4); }
      (s.snake || []).forEach((p, i) => { ctx.fillStyle = i === 0 ? "#a7f3d0" : "#34d399"; ctx.fillRect(p.x * CELL + 1, p.y * CELL + 1, CELL - 2, CELL - 2); });
    };
    if (status !== "playing") { draw(); return; }
    let raf, last = performance.now(), acc = 0;
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      acc += now - last; last = now;
      const interval = 1000 / s.speed;
      while (acc >= interval) {
        acc -= interval;
        dir.current = nextDir.current;
        const head = { x: s.snake[0].x + dir.current.x, y: s.snake[0].y + dir.current.y };
        if (head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS || s.snake.some((p) => p.x === head.x && p.y === head.y)) {
          cancelAnimationFrame(raf); sfx.explode();
          setBest((b) => { const nb = Math.max(b, scoreRef.current); localStorage.setItem("snake_best", nb); return nb; });
          setStatus("over"); return;
        }
        s.snake.unshift(head);
        if (head.x === s.food.x && head.y === s.food.y) { s.food = place(s.snake); s.speed = Math.min(16, s.speed + 0.3); scoreRef.current++; setScore(scoreRef.current); sfx.eat(); }
        else s.snake.pop();
      }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 flex gap-2"><AScore label="Score" value={score} /><AScore label="Best" value={best} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[300px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status === "over" && <AResult win={null} text={`Game Over · ${score}`} testId="snake-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <div className="mt-3 grid w-max grid-cols-3 gap-1.5">
        <span /><PadButton testId="snake-up" onPress={() => setD(0, -1)}><ArrowUp className="h-5 w-5" /></PadButton><span />
        <PadButton testId="snake-left" onPress={() => setD(-1, 0)}><ArrowLeft className="h-5 w-5" /></PadButton>
        <span />
        <PadButton testId="snake-right" onPress={() => setD(1, 0)}><ArrowRight className="h-5 w-5" /></PadButton>
        <span /><PadButton testId="snake-down" onPress={() => setD(0, 1)}><ArrowDown className="h-5 w-5" /></PadButton><span />
      </div>
    </div>
  );
}
